import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { SkeletonPlaceholder, SkeletonText, InlineNotification } from '@carbon/react';
import axiosInstance from '../utils/axiosInstance';
import LogoutModal from '../components/LogoutModal';
import { usePermissions } from '../contexts/PermissionContext';
// Reuse the established Employee Profile design system (emp-* tokens & components)
import './EmployeeProfile.css';
import './Profile.css';

// ===========================================
// SVG Icon Components (consistent with EmployeeProfile / ResidentProfile)
// ===========================================
const Icons = {
  User: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Mail: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  Phone: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
  Calendar: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  Key: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  ),
  Shield: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  ),
  CheckCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M22 4L12 14.01l-3-3"/>
    </svg>
  ),
  Info: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="16" x2="12" y2="12"/>
      <line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
  ),
  MapPin: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  Briefcase: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
    </svg>
  ),
  Clock: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12,6 12,12 16,14"/>
    </svg>
  ),
  Camera: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  Eye: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  Upload: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17,8 12,3 7,8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  X: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Moon: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  ),
  Sun: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  ),
  Logout: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16,17 21,12 16,7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
};

// ===========================================
// Image upload constraints (same as admin flow)
// ===========================================
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp'];

// ===========================================
// Utility Functions
// ===========================================
const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Srpska množina za broj dozvola (1 dozvola, 2-4 dozvole, 5+ dozvola)
const formatPermissionCount = (count) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} dozvola`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} dozvole`;
  return `${count} dozvola`;
};

const formatDate = (dateString) => {
  if (!dateString || dateString.startsWith('0001-01-01')) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return new Intl.DateTimeFormat('sr-Latn', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

// ===========================================
// Data Item (label + value) — matches EmployeeProfile
// ===========================================
const DataItem = ({ label, value, icon: Icon }) => (
  <div className="emp-data-item">
    <span className="emp-data-label">{label}</span>
    <span className="emp-data-value">
      {Icon && <span className="profile-data-icon"><Icon /></span>}
      {value || '-'}
    </span>
  </div>
);

// ===========================================
// Image Modal (full-size preview) — consistent with EmployeeProfile
// ===========================================
const ImageModal = ({ isOpen, onClose, imageSrc, altText }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="emp-image-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Uvećana fotografija"
    >
      <div className="emp-image-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="emp-image-modal__close"
          onClick={onClose}
          aria-label="Zatvori"
        >
          <Icons.X />
        </button>
        <img src={imageSrc} alt={altText} className="emp-image-modal__image" />
      </div>
    </div>
  );
};

// ===========================================
// Profile Page
// ===========================================
const Profile = () => {
  const { theme, toggleTheme } = useTheme();
  const { logout } = useAuth();
  // Administrator po definiciji ima sve dozvole (vidi PermissionContext),
  // pa se prazan odgovor sa /api/Permission/me ne prikazuje kao "nema dozvola".
  const { isAdmin } = usePermissions();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  // Dozvole prijavljenog korisnika (GET /api/Permission/me) — kartica "Moje dozvole"
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState(null);
  const fileInputRef = useRef(null);
  const avatarMenuRef = useRef(null);

  const handleLogoutConfirm = async () => {
    setIsLogoutModalOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    const fetchMe = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get('/api/Employee/me');
        const data = response.data?.data || response.data;
        if (!data) {
          throw new Error('Podaci profila nisu dostupni.');
        }
        setEmployee(data);
      } catch (err) {
        console.error('Greška pri učitavanju profila:', err);
        setError(
          err.response?.data?.message ||
          'Nije moguće učitati podatke profila. Pokušajte ponovo kasnije.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchMe();
  }, []);

  // Dozvole prijavljenog korisnika (GET /api/Permission/me).
  // Backend vraća niz objekata: [{ permission, name, label }, ...].
  // Podržavamo i goli niz stringova radi kompatibilnosti.
  useEffect(() => {
    // Administrator po definiciji ima sve dozvole - kartica "Moje dozvole" mu se
    // ne prikazuje, pa se ni endpoint ne poziva.
    if (isAdmin) {
      setUserPermissions([]);
      setPermissionsError(null);
      setPermissionsLoading(false);
      return;
    }

    const fetchPermissions = async () => {
      setPermissionsLoading(true);
      setPermissionsError(null);
      try {
        const response = await axiosInstance.get('/api/Permission/me');
        const data = response.data?.data ?? response.data;
        const list = Array.isArray(data?.permissions) ? data.permissions : [];
        setUserPermissions(
          list
            .map((item) =>
              typeof item === 'string'
                ? { permission: item, name: null, label: item }
                : {
                    permission: item?.permission,
                    name: item?.name || null,
                    label: item?.label || item?.name || item?.permission,
                  }
            )
            .filter((item) => Boolean(item.permission))
        );
      } catch (err) {
        console.error('Greška pri učitavanju dozvola:', err);
        setPermissionsError(
          err.response?.data?.message || 'Nije moguće učitati dozvole.'
        );
        setUserPermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchPermissions();
  }, [isAdmin]);

  // Upload a new profile image for the logged-in employee.
  // Uses the same endpoint as the admin flow: POST /api/Employee/{id}/upload-image
  const handleImageUpload = async (file) => {
    if (!file || !employee?.id) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setNotification({
        kind: 'error',
        title: 'Nepodržan format',
        subtitle: 'Dozvoljeni formati su JPG, PNG, WEBP, GIF i BMP.',
      });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setNotification({
        kind: 'error',
        title: 'Slika je prevelika',
        subtitle: 'Maksimalna dozvoljena veličina je 5MB.',
      });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    setIsUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post(
        `/api/Employee/${employee.id}/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const newImgSrc =
        response.data?.data?.url || response.data?.data?.imgSrc;

      if (response.data?.success && newImgSrc) {
        setEmployee((prev) => ({ ...prev, imgSrc: newImgSrc }));
        setNotification({
          kind: 'success',
          title: 'Fotografija ažurirana',
          subtitle: 'Vaša profilna fotografija je uspešno sačuvana.',
        });
        // Refresh the page shortly after the success notification is shown
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        throw new Error(response.data?.message || 'Greška pri uploadu slike');
      }
    } catch (err) {
      console.error('Greška pri uploadu slike:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle:
          err.response?.data?.message ||
          'Nije moguće uploadovati sliku. Pokušajte ponovo.',
      });
    } finally {
      setIsUploadingImage(false);
      setTimeout(() => setNotification(null), 4000);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    // Reset so selecting the same file again still triggers onChange
    e.target.value = '';
    if (file) handleImageUpload(file);
  };

  // Avatar click: no photo -> open upload directly; has photo -> show options menu
  const handleAvatarClick = () => {
    if (isUploadingImage) return;
    if (!employee?.imgSrc) {
      fileInputRef.current?.click();
      return;
    }
    setIsAvatarMenuOpen((prev) => !prev);
  };

  const handleViewImage = () => {
    setIsAvatarMenuOpen(false);
    setIsImageModalOpen(true);
  };

  const handleNewPhoto = () => {
    setIsAvatarMenuOpen(false);
    fileInputRef.current?.click();
  };

  // Close the avatar menu on outside click / Escape
  useEffect(() => {
    if (!isAvatarMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(event.target)) {
        setIsAvatarMenuOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsAvatarMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isAvatarMenuOpen]);

  const isDark = theme === 'g100' || theme === 'g90';

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className={`emp-page ${isDark ? 'emp-page--dark' : ''}`}>
        <div className="emp-container">
          <header className="emp-header profile-header emp-skeleton-header" role="banner" aria-hidden="true">
            <div className="emp-identity">
              <div className="emp-avatar-wrapper">
                <SkeletonPlaceholder className="emp-skeleton-avatar" />
              </div>
              <div className="emp-identity-content">
                <SkeletonText className="emp-skeleton-name" />
              </div>
            </div>
          </header>

          <main className="emp-main" aria-hidden="true">
            <div className="emp-general-info">
              <div className="emp-cards-grid">
                {[0, 1, 2, 3].map((card) => (
                  <div key={card} className="emp-card emp-skeleton-card">
                    <div className="emp-card__header">
                      <SkeletonText className="emp-skeleton-card-title" />
                    </div>
                    <div className="emp-card__body">
                      <div className="emp-data-list">
                        {[0, 1, 2].map((row) => (
                          <div key={row} className="emp-data-item">
                            <SkeletonText className="emp-skeleton-label" />
                            <SkeletonText className="emp-skeleton-value" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ---------- Error ----------
  if (error || !employee) {
    return (
      <div className={`emp-page ${isDark ? 'emp-page--dark' : ''}`}>
        <div className="emp-container">
          <div className="emp-empty-state">
            <div className="emp-empty-icon"><Icons.User /></div>
            <h2 className="emp-empty-title">Greška</h2>
            <p className="emp-empty-description">
              {error || 'Podaci profila nisu dostupni.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const fullName =
    employee.fullName ||
    `${employee.firstName || ''} ${employee.lastName || ''}`.trim() ||
    'Zaposleni';
  const role = employee.employeeTypeName || employee.role || 'Zaposleni';
  const isRegistered = employee.isRegistered === true;

  return (
    <div className={`emp-page ${isDark ? 'emp-page--dark' : ''}`}>
      {/* Floating Notification */}
      {notification && (
        <div className="profile-notification">
          <InlineNotification
            kind={notification.kind}
            title={notification.title}
            subtitle={notification.subtitle}
            onCloseButtonClick={() => setNotification(null)}
            hideCloseButton={false}
            lowContrast
          />
        </div>
      )}

      <div className="emp-container">
        {/* Image Preview Modal */}
        <ImageModal
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          imageSrc={employee.imgSrc}
          altText={`Fotografija: ${fullName}`}
        />

        {/* Header */}
        <header className="emp-header profile-header" role="banner">
          <div className="emp-identity">
            <div className="emp-avatar-wrapper profile-avatar-wrapper" ref={avatarMenuRef}>
              <button
                type="button"
                className="profile-avatar-btn"
                onClick={handleAvatarClick}
                disabled={isUploadingImage}
                aria-haspopup={employee.imgSrc ? 'menu' : undefined}
                aria-expanded={employee.imgSrc ? isAvatarMenuOpen : undefined}
                aria-label={employee.imgSrc ? 'Opcije fotografije' : 'Dodaj fotografiju'}
                title={employee.imgSrc ? 'Opcije fotografije' : 'Dodaj fotografiju'}
              >
                {employee.imgSrc ? (
                  <img
                    src={employee.imgSrc}
                    alt={`Fotografija: ${fullName}`}
                    className="emp-avatar profile-avatar"
                  />
                ) : (
                  <div className="emp-avatar emp-avatar--placeholder profile-avatar" aria-hidden="true">
                    {getInitials(fullName)}
                  </div>
                )}
                <span className="profile-avatar-badge" aria-hidden="true">
                  {isUploadingImage ? (
                    <span className="profile-avatar-spinner" />
                  ) : (
                    <Icons.Camera />
                  )}
                </span>
              </button>

              {/* Avatar options menu (only when a photo exists) */}
              {isAvatarMenuOpen && employee.imgSrc && (
                <div className="profile-avatar-menu" role="menu" aria-label="Opcije fotografije">
                  <button
                    type="button"
                    className="profile-avatar-menu__item"
                    role="menuitem"
                    onClick={handleViewImage}
                  >
                    <span className="profile-avatar-menu__icon"><Icons.Eye /></span>
                    Pogledaj sliku
                  </button>
                  <button
                    type="button"
                    className="profile-avatar-menu__item"
                    role="menuitem"
                    onClick={handleNewPhoto}
                  >
                    <span className="profile-avatar-menu__icon"><Icons.Upload /></span>
                    Nova profilna
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                className="profile-avatar-input"
                accept=".jpg,.jpeg,.png,.webp,.gif,.bmp"
                onChange={handleFileChange}
                tabIndex={-1}
                aria-hidden="true"
              />
            </div>
            <div className="emp-identity-content">
              <span className="profile-eyebrow">Moj profil</span>
              <div className="emp-identity-primary">
                <h1 className="emp-name">{fullName}</h1>
                <span
                  className={`emp-account-status ${
                    isRegistered
                      ? 'emp-account-status--registered'
                      : 'emp-account-status--unregistered'
                  }`}
                >
                  {isRegistered ? <Icons.CheckCircle /> : <Icons.Info />}
                  {isRegistered ? 'Registrovan' : 'Nije registrovan'}
                </span>
              </div>
              <div className="emp-identity-meta">
                <span className="emp-meta-chip">
                  <Icons.Briefcase />
                  {role}
                </span>
                {employee.username && (
                  <span className="emp-meta-chip">
                    <Icons.Key />
                    {employee.username}
                  </span>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="emp-main">
          <div className="emp-general-info">
            <div className="emp-cards-grid">
              {/* Identitet */}
              <div className="emp-card">
                <div className="emp-card__header">
                  <h3 className="emp-card__title">
                    <span className="emp-card__title-icon"><Icons.User /></span>
                    Identitet
                  </h3>
                </div>
                <div className="emp-card__body">
                  <div className="emp-data-list">
                    <DataItem label="Ime" value={employee.firstName} />
                    <DataItem label="Prezime" value={employee.lastName} />
                    <DataItem label="Tip zaposlenog" value={role} />
                  </div>
                </div>
              </div>

              {/* Kontakt */}
              <div className="emp-card">
                <div className="emp-card__header">
                  <h3 className="emp-card__title">
                    <span className="emp-card__title-icon"><Icons.Mail /></span>
                    Kontakt
                  </h3>
                </div>
                <div className="emp-card__body">
                  <div className="emp-data-list">
                    <DataItem label="Email" value={employee.email} icon={Icons.Mail} />
                    <DataItem
                      label="Telefon"
                      value={employee.phoneNumber || employee.phone}
                      icon={Icons.Phone}
                    />
                    <DataItem label="Adresa" value={employee.address} icon={Icons.MapPin} />
                  </div>
                </div>
              </div>

              {/* Zaposlenje */}
              <div className="emp-card">
                <div className="emp-card__header">
                  <h3 className="emp-card__title">
                    <span className="emp-card__title-icon"><Icons.Calendar /></span>
                    Zaposlenje
                  </h3>
                </div>
                <div className="emp-card__body">
                  <div className="emp-data-list">
                    <DataItem
                      label="Datum zaposlenja"
                      value={formatDate(employee.hireDate || employee.startDate)}
                      icon={Icons.Calendar}
                    />
                    <DataItem
                      label="Profil kreiran"
                      value={formatDate(employee.createdAt)}
                      icon={Icons.Clock}
                    />
                    <DataItem
                      label="Poslednja izmena"
                      value={formatDate(employee.updatedAt)}
                      icon={Icons.Clock}
                    />
                  </div>
                </div>
              </div>

              {/* Nalog */}
              <div className="emp-card">
                <div className="emp-card__header">
                  <h3 className="emp-card__title">
                    <span className="emp-card__title-icon"><Icons.Key /></span>
                    Nalog
                  </h3>
                </div>
                <div className="emp-card__body">
                  <div className="emp-data-list">
                    <div className="emp-data-item">
                      <span className="emp-data-label">Korisničko ime</span>
                      <span className="emp-data-value emp-data-value--row">
                        <span className="emp-account-username">
                          {employee.username || '-'}
                        </span>
                        <span
                          className={`emp-account-status ${
                            isRegistered
                              ? 'emp-account-status--registered'
                              : 'emp-account-status--unregistered'
                          }`}
                        >
                          {isRegistered ? <Icons.CheckCircle /> : <Icons.Info />}
                          {isRegistered ? 'Registrovan' : 'Nije registrovan'}
                        </span>
                      </span>
                    </div>
                    <DataItem label="Status naloga" value={isRegistered ? 'Aktivan' : 'Neaktivan'} />
                  </div>
                </div>
              </div>

              {/* Moje dozvole (GET /api/Permission/me) - administratoru se ne prikazuje
                  jer po definiciji ima sve dozvole u sistemu. */}
              {!isAdmin && (
                <div className="emp-card profile-permissions-card">
                  <div className="emp-card__header">
                    <h3 className="emp-card__title">
                      <span className="emp-card__title-icon"><Icons.Shield /></span>
                      Moje dozvole
                    </h3>
                    {!permissionsLoading && !permissionsError && userPermissions.length > 0 && (
                      <div className="emp-card__actions">
                        <span className="emp-permissions-count">
                          {formatPermissionCount(userPermissions.length)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="emp-card__body">
                    {permissionsLoading ? (
                      <div className="profile-permissions-grid">
                        {[0, 1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className="emp-permission-check emp-permission-check--skeleton"
                          >
                            <SkeletonPlaceholder className="emp-permission-skeleton-box" />
                            <SkeletonText className="emp-permission-skeleton-label" />
                          </div>
                        ))}
                      </div>
                    ) : permissionsError ? (
                      <div className="emp-permissions-empty">
                        <Icons.Info />
                        <p>{permissionsError}</p>
                      </div>
                    ) : userPermissions.length === 0 ? (
                      <div className="emp-permissions-empty">
                        <Icons.Shield />
                        <p>Trenutno nemate dodeljenih dozvola.</p>
                      </div>
                    ) : (
                      <div className="profile-permissions-grid">
                        {userPermissions.map((permission) => (
                          <div
                            key={permission.permission}
                            className="profile-permission"
                            title={permission.name || undefined}
                          >
                            <span className="profile-permission__icon">
                              <Icons.CheckCircle />
                            </span>
                            <span className="profile-permission__text">
                              <span className="profile-permission__label">
                                {permission.label}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Podešavanja */}
              <div className="emp-card">
                <div className="emp-card__header">
                  <h3 className="emp-card__title">
                    <span className="emp-card__title-icon"><Icons.Sun /></span>
                    Podešavanja
                  </h3>
                </div>
                <div className="emp-card__body">
                  <div className="profile-settings-list">
                    <button
                      type="button"
                      className="profile-settings-item"
                      onClick={toggleTheme}
                    >
                      <span className="profile-settings-item__icon">
                        {isDark ? <Icons.Sun /> : <Icons.Moon />}
                      </span>
                      <span className="profile-settings-item__text">
                        <span className="profile-settings-item__label">Tema</span>
                        <span className="profile-settings-item__value">
                          {isDark ? 'Tamna' : 'Svetla'}
                        </span>
                      </span>
                      <span className="profile-settings-item__action">
                        {isDark ? 'Prebaci na svetlu' : 'Prebaci na tamnu'}
                      </span>
                    </button>

                    <button
                      type="button"
                      className="profile-settings-item profile-settings-item--danger"
                      onClick={() => setIsLogoutModalOpen(true)}
                    >
                      <span className="profile-settings-item__icon">
                        <Icons.Logout />
                      </span>
                      <span className="profile-settings-item__text">
                        <span className="profile-settings-item__label">Odjava</span>
                        <span className="profile-settings-item__value">
                          Odjavite se sa naloga
                        </span>
                      </span>
                      <span className="profile-settings-item__action">Odjavi se</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <LogoutModal
        open={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogoutConfirm}
      />
    </div>
  );
};

export default Profile;
